<script lang="ts">
	import { format } from 'svelte-i18n';

	import type { Device } from 'src/types/Device';

	import { devices_settings, devices_status } from 'src/stores';

	import FactoryResetModal from 'src/components/FactoryResetModal.svelte';

	import { Alert, Button, toast } from '@Shelly/shelly-design-system';

	import { deleteAllChannelsInterfaceData } from 'src/helpers/add_delete_helpers';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import {
		findZWaveGatewayForDevice,
		getZWaveDeviceDisplayName,
		isZWaveRemoveFailedActionEnabled,
	} from 'src/helpers/z_wave_gateway_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { zwaveDeviceRemoveFailed } from 'src/services/xz_http_reqs';

	export let device: Device;

	$: status = $devices_status?.[real_id(device?.id)];
	$: settings = $devices_settings?.[real_id(device.id)];
	$: displayName = getZWaveDeviceDisplayName(device, status, settings);
	$: zwaveGateway = findZWaveGatewayForDevice(device, status);
	$: zwaveGatewayStatus = zwaveGateway ? $devices_status[real_id(zwaveGateway.id)] : undefined;
	$: isRemoveFailedEnabled = isZWaveRemoveFailedActionEnabled(device, status, zwaveGatewayStatus);

	let showModal = false;
	let loading = false;

	const removeFailedDevice = async () => {
		if (loading || !isRemoveFailedEnabled) return;
		loading = true;

		const res = await zwaveDeviceRemoveFailed(device);
		if (!res?.isok) {
			toast.open({
				type: 'danger',
				title: $format('zwave_remove_failed_err_toast', {
					default: 'Failed to remove the failed Z-Wave device.',
				}),
			});
			loading = false;
			return;
		}

		const interfaceRes = await deleteAllChannelsInterfaceData(device, 'deleted');
		if (interfaceRes?.isok) {
			toast.open({
				type: 'success',
				title: $format('zwave_remove_failed_scc_toast', {
					default: 'Failed Z-Wave device was removed.',
				}),
			});
			resetDrawerClose();
		} else {
			toast.open({
				type: 'danger',
				title: $format('zwave_remove_failed_cloud_err_toast', {
					default:
						'Device was removed from the Z-Wave network, but cloud cleanup failed. Try deleting it again.',
				}),
			});
		}

		loading = false;
	};

	$: modalProps = {
		title: $format('prompt_title_zwave_remove_failed', {
			default: 'Remove failed device?',
		}),
		name: displayName,
		text: $format('prompt_text_zwave_remove_failed', {
			values: { device_name: displayName },
			default:
				'Use this only when the device is unreachable. The gateway will force-remove ‘{device_name}’ from the Z-Wave network and it will be deleted from your account.',
		}),
		alert: {
			heading: $format('alert_heading_zwave_remove_failed', {
				default:
					'This cannot be undone. Prefer exclusion or factory reset when the device still responds.',
			}),
			type: 'danger' as const,
		},
		button_1: $format('button_zwave_remove_failed', { default: 'Remove' }),
		button_1_variant: 'danger' as const,
		button_2: $format('button_cancel', { default: 'Cancel' }),
		callback: removeFailedDevice,
	};
</script>

{#if isRemoveFailedEnabled}
	<Alert
		class="mb-s"
		type="warning"
		heading={$format('alert_message_zwave_remove_failed', {
			default:
				'Removes an unresponsive Z-Wave node from the gateway network. Prefer factory reset when the device is online.',
		})}
	/>
{:else}
	<Alert
		class="mb-s"
		type="info"
		heading={$format('alert_message_zwave_remove_failed_disabled', {
			default:
				'Available only when this device is offline or unresponsive and its Wave gateway is online.',
		})}
	/>
{/if}

<Button
	{loading}
	disabled={!isRemoveFailedEnabled}
	size="m"
	width="full"
	variant="danger"
	on:click={() => (showModal = true)}
>
	{capFirstChar($format('button_zwave_remove_failed', { default: 'Remove' }))}
</Button>

{#if showModal}
	<FactoryResetModal bind:showModal props={modalProps} />
{/if}
